/**
 * Geometry for placing conjugation site markers on the antibody glyph: mapping between a domain's own
 * coordinates and the viewbox, and finding a domain's edges and lines.
 *
 * These are self-contained calculations whose results are easiest to check by looking at the drawing. The
 * decisions about which markers go where are in conjugationSiteMarkers.
 */

import {
  AntibodyFormat,
  AntibodyFormatDomainVisualization,
  BlockDomainVisualization,
  FusionDomainVisualization,
  HingeDomainVisualization,
} from 'antibodies/AntibodyTypedefs';
import {getHingePathPoints} from 'antibodies/visualization/AntibodyHinge';
import {getJoinerPathParameters} from 'antibodies/visualization/AntibodyJoiner';
import {getLinkerPathParameters} from 'antibodies/visualization/AntibodyLinker';
import {PathParameterType, toPathParameters} from 'antibodies/visualization/AntibodyPathLinker';
import {LinePath, linePathFromParameters, Point} from 'antibodies/visualization/linePathGeometry';
import {assert} from 'Asserts';

/**
 * Map a point in a domain's local coordinate space to viewbox coordinates, mirroring the
 * transform Antibody.tsx applies to each domain `<g>`.
 *
 * Antibody.tsx sets `transform={translate(tx,ty) rotate(r) scale(sx,1)}` on the group but also
 * applies `transform-origin: center; transform-box: content-box` (the `rotatableComponent` CSS
 * class), so the rotate/scale pivot around the center `(cx, cy)` of the domain's bounding box, not the
 * local origin. The effective transform is therefore
 * `translate(cx,cy) · translate(tx,ty) rotate(r) scale(sx,1) · translate(-cx,-cy)`.
 *
 * @param local The point, in the domain's own coordinate space.
 * @param center The center of the domain's bounding box, in the domain's own coordinate space.
 */
export function applyDomainTransform(
  viz: AntibodyFormatDomainVisualization,
  local: Point,
  center: Point
): Point {
  const scaleX = viz.reflectX ? -1 : 1;

  // Center-relative point, then scale (reflect) about the center.
  const scaledX = scaleX * (local.x - center.x);
  const scaledY = local.y - center.y;

  const radians = ((viz.rotate ?? 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const rotatedX = scaledX * cos - scaledY * sin;
  const rotatedY = scaledX * sin + scaledY * cos;

  // Undo the center offset (into the group's frame) then apply the group translate.
  return {
    x: viz.translate.x + center.x + rotatedX,
    y: viz.translate.y + center.y + rotatedY,
  };
}

export function isBoxDomain(
  viz: AntibodyFormatDomainVisualization
): viz is BlockDomainVisualization | FusionDomainVisualization {
  return (
    viz.__typename === 'BlockDomainVisualizationNode' || viz.__typename === 'FusionDomainVisualizationNode'
  );
}

/**
 * The center of a chain domain's box, in the domain's own coordinate space. The box runs from (0, 0) to
 * (width, height).
 */
export function boxCenter(format: AntibodyFormat): Point {
  const {width, height} = format.visualization.sizing.chainDomainSizing;
  return {x: width / 2, y: height / 2};
}

/** The geometry of a hinge, which runs from the outer end of its short part, through the joint, to the end of its long part. */
export function hingeLinePath(viz: HingeDomainVisualization): LinePath {
  const {start, joint, end} = getHingePathPoints(viz);
  return linePathFromParameters(
    [
      {type: PathParameterType.LINE, dx: joint.x - start.x, dy: joint.y - start.y},
      {type: PathParameterType.LINE, dx: end.x - joint.x, dy: end.y - joint.y},
    ],
    start
  );
}

/**
 * The geometry of a domain drawn as a line (a hinge, linker, joiner, or the like), or null for a domain
 * that isn't. Lines are what AntibodyPathLinker and the components built on it draw.
 */
export function linePathOf(viz: AntibodyFormatDomainVisualization): LinePath {
  switch (viz.__typename) {
    case 'HingeDomainVisualizationNode':
      return hingeLinePath(viz);
    case 'LinkerDomainVisualizationNode':
      return linePathFromParameters(getLinkerPathParameters(viz));
    case 'JoinerDomainVisualizationNode':
      return linePathFromParameters(getJoinerPathParameters(viz));
    case 'PathLinkerDomainVisualizationNode':
      return linePathFromParameters(toPathParameters(viz.pathParameters));
    case 'StraightLineLinkerDomainVisualizationNode':
      return linePathFromParameters([{type: PathParameterType.LINE, dx: 0, dy: viz.centerLength}]);
    default:
      assert(false, `Non-line domain visualization type: ${viz.__typename}`);
  }
}

/** Where the center of a domain's bounding box is in the viewbox. */
function viewboxCenterOf(format: AntibodyFormat, viz: AntibodyFormatDomainVisualization): Point {
  const center = isBoxDomain(viz) ? boxCenter(format) : linePathOf(viz)?.center;
  // Rotating and reflecting about the center leaves it where it is, so only the translation applies.
  return {x: viz.translate.x + center.x, y: viz.translate.y + center.y};
}

function meanOf(points: ReadonlyArray<Point>): Point {
  return {
    x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
  };
}

/**
 * The local x of the long edge of a chain domain's box where site specific conjugation sites are placed. This
 * edge faces away from what the domain interacts with, e.g. away from the paired light chain for a heavy
 * chain's VH, and away from the other heavy chain for a CH3.
 *
 * If there are no interactions, the edge faces away from the center of the antibody.
 */
export function outsideEdgeLocalX(
  format: AntibodyFormat,
  viz: BlockDomainVisualization | FusionDomainVisualization
): number {
  const {width, height} = format.visualization.sizing.chainDomainSizing;
  const vizByRef = new Map(format.visualization.domainVisualizations.map((v) => [v.ref, v]));
  const partnerRefs = format.interactions
    .filter((interaction) => interaction.domainRefs.includes(viz.ref))
    .flatMap((interaction) => interaction.domainRefs.filter((ref) => ref !== viz.ref));
  // If there are no interactions, the anchor is the average of all domains (the center of the antibody).
  const anchorRefs = partnerRefs.length > 0 ? partnerRefs : Array.from(vizByRef.keys());
  const anchor = meanOf(
    anchorRefs.map((ref) => {
      const anchorViz = vizByRef.get(ref);
      assert(anchorViz != null, `A domain interacts with a domain the antibody format does not draw: ${ref}`);
      return viewboxCenterOf(format, anchorViz);
    })
  );

  const distanceFromAnchor = (localX: number): number => {
    const edgeMiddle = applyDomainTransform(viz, {x: localX, y: height / 2}, boxCenter(format));
    return Math.hypot(edgeMiddle.x - anchor.x, edgeMiddle.y - anchor.y);
  };
  return distanceFromAnchor(0) > distanceFromAnchor(width) ? 0 : width;
}

/**
 * Map a point in the viewbox back into the local coordinate space of a chain domain's box: the inverse
 * of {@link applyDomainTransform}.
 */
function viewboxToBoxLocal(
  format: AntibodyFormat,
  box: AntibodyFormatDomainVisualization,
  point: Point
): Point {
  const center = boxCenter(format);
  const radians = ((box.rotate ?? 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const rotatedX = point.x - box.translate.x - center.x;
  const rotatedY = point.y - box.translate.y - center.y;
  const scaledX = rotatedX * cos + rotatedY * sin;
  const scaledY = -rotatedX * sin + rotatedY * cos;
  const scaleX = box.reflectX ? -1 : 1;
  return {x: center.x + scaleX * scaledX, y: center.y + scaledY};
}

// How many evenly spaced points along a line are checked for being hidden behind a chain domain.
const HIDDEN_LINE_SAMPLES = 200;

/**
 * The range of distances along a line that isn't hidden behind a chain domain. Lines are drawn under the
 * chain domains, and a line often runs a little way under the domain it is attached to, so its ends can
 * be hidden.
 *
 * Hiddenness is checked at evenly spaced points along the line, by mapping each point into the frame of
 * each chain domain's box and testing it against the box (grown by `margin`). Only the hidden stretches at
 * the two ends are left out: a line that is covered somewhere in the middle keeps that part.
 *
 * @param margin How far from a box, in viewbox units, a point still counts as hidden.
 */
export function visibleRangeOfLine(
  format: AntibodyFormat,
  viz: AntibodyFormatDomainVisualization,
  path: LinePath,
  margin: number
): {start: number; end: number} {
  const {width, height} = format.visualization.sizing.chainDomainSizing;
  const boxes = format.visualization.domainVisualizations.filter((v) => isBoxDomain(v));
  const isHidden = (distance: number): boolean => {
    const point = applyDomainTransform(viz, path.pointAndNormalAt(distance).point, path.center);
    return boxes.some((box) => {
      const local = viewboxToBoxLocal(format, box, point);
      return (
        local.x >= -margin && local.x <= width + margin && local.y >= -margin && local.y <= height + margin
      );
    });
  };

  const stepLength = path.length / HIDDEN_LINE_SAMPLES;
  let first = 0;
  while (first <= HIDDEN_LINE_SAMPLES && isHidden(first * stepLength)) {
    first++;
  }
  assert(
    first <= HIDDEN_LINE_SAMPLES,
    `A line that conjugation sites are on is entirely hidden behind chain domains: ${viz.__typename} ${viz.ref}`
  );
  let last = HIDDEN_LINE_SAMPLES;
  while (last > first && isHidden(last * stepLength)) {
    last--;
  }
  return {start: first * stepLength, end: last * stepLength};
}

/**
 * The position in the viewbox of a point `offset` to one side of a line, `along` it. Which side is
 * positive is fixed for each line, and is the same for every copy of it.
 */
export function besideLine(
  viz: AntibodyFormatDomainVisualization,
  path: LinePath,
  along: number,
  offset: number
): Point {
  const {point, normal} = path.pointAndNormalAt(along);
  return applyDomainTransform(
    viz,
    {x: point.x + normal.x * offset, y: point.y + normal.y * offset},
    path.center
  );
}
